import React, { useEffect, useState } from 'react';
import DateInput from '../../components/DateInput/DateInput';

// Contenido de cada alerta con panel: reglas acordadas de encendido, apagado y destinatario.
const ALERTAS = {
    coleta: {
        titulo: 'Coleta (DLD)',
        descripcion: 'Bookings cuyo plazo para retirar los contenedores vacíos (DLD) está encima y la coleta aún no está agendada con el proveedor.',
        enciende: 'DLD en 5 días o menos y coleta “Sin agendar”',
        apaga: 'Al registrar la fecha de coleta agendada',
        avisa: 'Logística responsable del booking',
        pie: 'Plazo: DLD en 5 días o menos (incluye vencidas).',
        genero: 'a', // "Vencida"
    },
    dlc: {
        titulo: 'Cut-off de carga (DLC)',
        descripcion: 'Bookings cuyo plazo para entregar los contenedores llenos en el puerto (DLC) está encima y todavía hay HC fuera del puerto.',
        enciende: 'DLC en 3 días o menos y HC en puerto menor al total',
        apaga: 'Cuando todos los HC tienen Gate In',
        avisa: 'Logística y forwarder (opcional)',
        pie: 'Plazo: DLC en 3 días o menos (incluye vencidos).',
        genero: 'o', // "Vencido"
    },
};

const fmtDiaMes = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');

const etiquetaPlazo = (dias, genero) => {
    if (dias < 0) return `Vencid${genero} hace ${-dias} ${-dias === 1 ? 'día' : 'días'}`;
    if (dias === 0) return 'Vence hoy';
    return `Faltan ${dias} ${dias === 1 ? 'día' : 'días'}`;
};

const PanelAlerta = ({ tipo, items, onCerrar, onFicha, onRegistrarColeta }) => {
    const config = ALERTAS[tipo];
    const [fechas, setFechas] = useState({});
    const [transportistas, setTransportistas] = useState({});
    const [guardando, setGuardando] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        const alPresionar = (e) => { if (e.key === 'Escape') onCerrar(); };
        document.addEventListener('keydown', alPresionar);
        return () => document.removeEventListener('keydown', alPresionar);
    }, [onCerrar]);

    const registrar = async (item) => {
        setError('');
        setGuardando(item.id_solicitud);
        try {
            await onRegistrarColeta(item.id_solicitud, fechas[item.id_solicitud], transportistas[item.id_solicitud] || '');
        } catch (e) {
            setError(e.message);
        } finally {
            setGuardando(null);
        }
    };

    return (
        <div className="ap-overlay" onClick={onCerrar}>
            <aside
                className="ap-panel"
                role="dialog"
                aria-label={config.titulo}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ap-cabecera">
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="ui-eyebrow">Alerta · Plazo</div>
                        <button type="button" className="ap-cerrar" onClick={onCerrar} aria-label="Cerrar panel">✕</button>
                    </div>
                    <h4 className="ui-title mb-2">
                        {config.titulo} <span className="ap-contador">{items.length}</span>
                    </h4>
                    <p className="ui-descripcion">{config.descripcion}</p>
                    <div className="ap-reglas">
                        <div><strong>Se enciende</strong><span>{config.enciende}</span></div>
                        <div><strong>Se apaga</strong><span>{config.apaga}</span></div>
                        <div><strong>Avisa a</strong><span>{config.avisa}</span></div>
                    </div>
                </div>

                <div className="ap-cuerpo">
                    {items.length === 0 && (
                        <div className="ce-muted text-center py-4">No hay bookings con esta alerta.</div>
                    )}

                    {items.map((item) => (
                        <div key={item.id_solicitud} className="ap-card">
                            <div className="d-flex justify-content-between align-items-center gap-2">
                                <span className="ce-mono fw-bold">
                                    {item.nro_booking || `Sin N° (${item.ffw || '—'})`}
                                </span>
                                <span className={`ce-pill ${item.dias <= 0 ? 'ce-pill--sin' : 'ce-pill--solicitado'}`}>
                                    {etiquetaPlazo(item.dias, config.genero)}
                                </span>
                            </div>
                            <div className="fw-semibold my-1">
                                {[item.ffw, item.carrier].filter(Boolean).join(' · ') || '—'} · {item.pol || '—'} → {item.pod || '—'}
                            </div>

                            {tipo === 'coleta' ? (
                                <div className="ap-datos">
                                    <div><span>DLD</span><strong className="ce-mono">{fmtDiaMes(item.dld)}</strong></div>
                                    <div><span>ETD</span><strong className="ce-mono">{fmtDiaMes(item.etd)}</strong></div>
                                    <div><span>HC</span><strong className="ce-mono">{item.hc}</strong></div>
                                    <div>
                                        <span>Proveedor</span>
                                        <strong>{item.proveedores.length > 0 ? item.proveedores.join(', ') : 'Sin asignar'}</strong>
                                    </div>
                                </div>
                            ) : (
                                <div className="ap-datos">
                                    <div><span>DLC</span><strong className="ce-mono">{fmtDiaMes(item.dlc)}</strong></div>
                                    <div><span>ETD</span><strong className="ce-mono">{fmtDiaMes(item.etd)}</strong></div>
                                    <div><span>En puerto</span><strong className="ce-mono">{item.en_puerto} de {item.hc}</strong></div>
                                    <div><span>Nave</span><strong>{item.nave || 'Por confirmar'}</strong></div>
                                </div>
                            )}

                            <div className="ap-accion">
                                {tipo === 'coleta' ? (
                                    <>
                                        <label className="ce-filtro flex-grow-1">
                                            Fecha de coleta agendada
                                            <DateInput
                                                name={`coleta-${item.id_solicitud}`}
                                                value={fechas[item.id_solicitud] || ''}
                                                onChange={(e) => setFechas((actual) => ({ ...actual, [item.id_solicitud]: e.target.value }))}
                                            />
                                        </label>
                                        <label className="ce-filtro flex-grow-1">
                                            Transportista
                                            <input
                                                type="text"
                                                maxLength={255}
                                                placeholder="Opcional"
                                                value={transportistas[item.id_solicitud] || ''}
                                                onChange={(e) => setTransportistas((actual) => ({ ...actual, [item.id_solicitud]: e.target.value }))}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className="ui-btn ui-btn-primary"
                                            disabled={!fechas[item.id_solicitud] || guardando === item.id_solicitud}
                                            onClick={() => registrar(item)}
                                        >
                                            {guardando === item.id_solicitud ? 'Guardando…' : 'Registrar'}
                                        </button>
                                    </>
                                ) : (
                                    <span className="ce-muted flex-grow-1">El Gate In se registra por contenedor en la ficha.</span>
                                )}
                                <button type="button" className="ce-accion" onClick={() => onFicha(item.id_solicitud)}>Ficha</button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="ap-pie">
                    {error ? <span className="error">{error}</span> : <span className="ce-muted">{config.pie}</span>}
                </div>
            </aside>
        </div>
    );
};

export default PanelAlerta;
